import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, LogOut, ChevronDown } from "lucide-react";
import { redirectToLogin } from "../../utils/auth";
import { useUser } from "../../context/UserContext";

// Route → readable page title, shown in the header now that navigation lives in the sidebar.
const PAGE_TITLES = [
  { prefix: "/Dashboard", title: "Support Tickets" },
  { prefix: "/Form", title: "New Ticket" },
  { prefix: "/Ticket", title: "Ticket Details" },
  { prefix: "/LOA/new", title: "Upload New LOA" },
  { prefix: "/LOA", title: "LOA Management" },
  { prefix: "/Resources", title: "Resources" },
  { prefix: "/Resource", title: "Resource Detail" },
  { prefix: "/Analytics", title: "Analytics" },
  { prefix: "/Workload", title: "Workload" },
  { prefix: "/ReleaseNotes", title: "Release Notes" },
];

const getPageTitle = (pathname) =>
  PAGE_TITLES.find(({ prefix }) => pathname.startsWith(prefix))?.title ?? "";

// Falls back to a formatted email local-part when the user has no display name
// (e.g. partners, or a session hydrated before `name` was cached).
const displayName = (name, email) => {
  if (name) return name;
  const local = (email || "").split("@")[0];
  return local
    .split(".")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const initials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useUser();
  const { username, name, isPartner } = user;
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const resolvedName = displayName(name, username);
  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="flex items-center h-[72px] w-full bg-jiojbm50 py-3 px-8 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-20 relative">
      <div
        className="flex items-center gap-x-3 cursor-pointer flex-shrink-0"
        onClick={() => navigate("/Dashboard")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="36"
          height="36"
          viewBox="0 0 44 44"
          fill="none"
        >
          <g clipPath="url(#clip0_325_90655)">
            <path
              d="M44.0001 22.0028C43.9895 34.097 34.2741 43.9576 22.0913 43.9998C9.81955 44.042 -0.0255884 34.1845 4.99612e-05 21.9244C0.0256884 9.68241 10.0865 -0.244517 22.4698 0.00433464C34.2002 0.241121 43.9789 9.79854 44.0001 22.0028ZM16.4705 28.7746C16.4705 29.7414 16.4674 30.693 16.4735 31.6462C16.4735 31.8061 16.484 31.9765 16.5353 32.1258C16.8354 33.0156 17.8775 33.349 18.695 32.8075C20.6314 31.524 22.5588 30.227 24.4983 28.9511C24.6823 28.8304 24.9326 28.7641 25.1558 28.7535C26.1949 28.7052 27.2567 28.8018 28.2747 28.6343C33.1791 27.8244 36.2105 23.4748 35.752 19.0196C35.5137 16.6985 34.5123 14.7695 32.8157 13.2146C31.1356 11.6747 29.1011 10.9568 26.8344 10.9417C23.6688 10.9206 20.5017 10.9252 17.3346 10.9463C16.662 10.9508 15.9788 11.0156 15.3182 11.1469C9.5134 12.2901 6.42171 18.7949 9.21931 24.0087C10.7546 26.8698 13.2189 28.3825 16.4705 28.7776V28.7746Z"
              fill="#6D17CE"
            />
            <path
              d="M16.4699 28.7748C13.2184 28.3797 10.7526 26.8654 9.21878 24.0059C6.42118 18.7921 9.51287 12.2872 15.3177 11.144C15.9783 11.0143 16.6599 10.948 17.3341 10.9434C20.4997 10.9223 23.6668 10.9193 26.8339 10.9389C29.1006 10.954 31.1366 11.6719 32.8151 13.2118C34.5118 14.7667 35.5132 16.6957 35.7515 19.0168C36.21 23.4735 33.1786 27.8216 28.2741 28.6315C27.2561 28.7989 26.1959 28.7024 25.1553 28.7507C24.9321 28.7612 24.6817 28.8276 24.4978 28.9482C22.5568 30.2257 20.6309 31.5227 18.6944 32.8047C17.8785 33.3461 16.8349 33.0128 16.5348 32.123C16.4835 31.9737 16.4744 31.8033 16.4729 31.6434C16.4669 30.6902 16.4699 29.7385 16.4699 28.7718V28.7748ZM22.0214 21.267C20.1875 21.267 18.3521 21.2655 16.5182 21.267C15.7415 21.267 15.1548 21.8356 15.1578 22.5716C15.1593 23.3031 15.7551 23.8747 16.5287 23.8747C20.1724 23.8762 23.8161 23.8762 27.4597 23.8747C28.2485 23.8747 28.8533 23.288 28.8382 22.5399C28.8231 21.8522 28.2168 21.2399 27.4507 21.255C25.6424 21.2881 23.8327 21.2655 22.0229 21.2655L22.0214 21.267ZM18.5768 18.4437C19.2434 18.4437 19.9085 18.4542 20.5751 18.4407C21.5116 18.4226 22.1466 17.6217 21.9113 16.7741C21.7318 16.1286 21.2236 15.7802 20.4107 15.7757C19.18 15.7682 17.9479 15.7636 16.7173 15.7817C16.4367 15.7863 16.1366 15.8466 15.8833 15.9642C15.3554 16.2085 15.09 16.8269 15.2151 17.3804C15.3614 18.0229 15.8531 18.4271 16.5423 18.4407C17.221 18.4542 17.8996 18.4437 18.5783 18.4437H18.5768Z"
              fill="white"
            />
            <path
              d="M22.0223 21.2671C23.8321 21.2671 25.6418 21.2897 27.4501 21.2566C28.2162 21.243 28.824 21.8538 28.8376 22.5416C28.8542 23.2896 28.2494 23.8763 27.4591 23.8763C23.8155 23.8778 20.1718 23.8778 16.5281 23.8763C15.7545 23.8763 15.1602 23.3047 15.1572 22.5732C15.1557 21.8372 15.7409 21.2686 16.5176 21.2686C18.3515 21.2656 20.1869 21.2686 22.0208 21.2686L22.0223 21.2671Z"
              fill="#6D17CE"
            />
            <path
              d="M18.5789 18.4437C17.9002 18.4437 17.2216 18.4543 16.5429 18.4407C15.8552 18.4271 15.362 18.0229 15.2157 17.3804C15.089 16.8269 15.356 16.2086 15.8838 15.9642C16.1372 15.8466 16.4373 15.7863 16.7178 15.7818C17.9485 15.7621 19.1806 15.7682 20.4113 15.7757C21.2226 15.7802 21.7324 16.1286 21.9119 16.7741C22.1471 17.6217 21.5122 18.4226 20.5757 18.4407C19.9106 18.4543 19.244 18.4437 18.5774 18.4437H18.5789Z"
              fill="#6D17CE"
            />
          </g>
          <defs>
            <clipPath id="clip0_325_90655">
              <rect width="44" height="44" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <div className="text-white font-bold text-lg leading-tight">JBM Support</div>
      </div>

      {pageTitle && (
        <>
          <div className="h-6 w-px bg-white/25 mx-5 flex-shrink-0" />
          <div className="text-white/90 font-semibold text-sm truncate">{pageTitle}</div>
        </>
      )}

      <div className="ml-auto flex items-center gap-6 flex-shrink-0">
        <div ref={buttonRef} className="relative">
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/10 transition-colors"
          >
            <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center font-bold text-jiojbm50 text-sm flex-shrink-0">
              {initials(resolvedName)}
            </div>
            <span className="font-semibold text-white text-sm max-w-[160px] truncate">
              {resolvedName}
            </span>
            <ChevronDown
              size={16}
              className={`text-white/80 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div
              ref={cardRef}
              className="absolute overflow-hidden flex flex-col z-30 top-12 right-0 w-56 shadow-[0_4px_16px_rgba(0,0,0,0.15)] bg-white rounded-xl border border-gray-100"
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="font-bold text-sm text-gray-800 truncate">{resolvedName}</div>
                <div className="text-xs text-gray-500 truncate">{username}</div>
              </div>

              {isPartner === false && (
                <button
                  type="button"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-jiojbm20 hover:text-jiojbm50 text-left"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(`/Resource/${username.split("@")[0]}`);
                  }}
                >
                  <LayoutDashboard size={18} />
                  My Dashboard
                </button>
              )}

              <button
                type="button"
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-jiojbm20 hover:text-jiojbm50 text-left"
                onClick={() => redirectToLogin()}
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
